import { Mail, ArrowRight, ArrowDown } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { PartnerSection } from "@/components/site/Sections";

const routes = [
  {
    tag: "For Suppliers",
    text: "International producers and manufacturers seeking market-development opportunities across the Middle East, India and Southeast Asia.",
    label: "Contact Procurement",
    href: "#partner-form",
    testid: "route-suppliers-button",
    primary: true,
  },
  {
    tag: "For Commercial Partners",
    text: "Distributors, retailers, foodservice businesses and strategic commercial partners across our destination markets.",
    label: "Contact Business Development",
    href: "mailto:procurement@rscimports.com?subject=Commercial%20Partnership%20Enquiry%20—%20RSC%20Imports",
    testid: "route-partners-button",
    primary: false,
  },
];

export default function Partner() {
  return (
    <>
      <section data-testid="partner-hero" className="pt-36 pb-16 md:pt-48 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold text-[#BFA26F] mb-5"
          >
            Partner With Us
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-[#111413] max-w-4xl leading-[1.1]"
          >
            Start a Conversation With RSC Imports.
          </motion.h1>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-7 h-px w-24 bg-[#BFA26F]"
          />
        </div>
      </section>

      <section data-testid="partner-routes-section" className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {routes.map((r, i) => (
              <Reveal key={r.tag} delay={i * 0.12}>
                <div className="h-full border border-[#0D2B1E]/10 bg-white p-8 md:p-12 flex flex-col hover:border-[#BFA26F] transition-colors duration-300" data-testid={`route-card-${r.tag.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#BFA26F] font-semibold mb-4">{r.tag}</p>
                  <p className="text-base md:text-lg font-light text-[#4A5D54] leading-relaxed mb-10 max-w-md">{r.text}</p>
                  <a
                    href={r.href}
                    data-testid={r.testid}
                    className={`group mt-auto inline-flex items-center gap-3 px-8 py-4 text-sm font-bold tracking-widest uppercase w-fit transition-colors duration-300 ${
                      r.primary
                        ? "bg-[#0D2B1E] text-[#FBF9F6] border border-[#0D2B1E] hover:bg-transparent hover:text-[#0D2B1E]"
                        : "border border-[#0D2B1E]/30 text-[#0D2B1E] hover:border-[#BFA26F] hover:text-[#BFA26F]"
                    }`}
                  >
                    {r.label}
                    {r.primary ? (
                      <ArrowDown size={16} strokeWidth={1.5} className="group-hover:translate-y-0.5 transition-transform duration-300" />
                    ) : (
                      <ArrowRight size={16} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
                    )}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <a
              href="mailto:procurement@rscimports.com"
              data-testid="partner-email-link"
              className="mt-12 inline-flex items-center gap-3 text-sm text-[#0D2B1E] hover:text-[#BFA26F] transition-colors duration-200"
            >
              <Mail size={16} strokeWidth={1.5} /> procurement@rscimports.com
            </a>
          </Reveal>
        </div>
      </section>

      <PartnerSection />
    </>
  );
}
